import {
  MouseEvent as ReactMouseEvent,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Add, Remove, RestartAlt } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';

type FOProfileImageViewModalProps = {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  title?: string;
};

const ImageViewModal = ({
  open,
  onClose,
  imageUrl,
}: FOProfileImageViewModalProps) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const scrollStartRef = useRef({ left: 0, top: 0 });

  const minZoom = 1;
  const maxZoom = 3;
  const zoomStep = 0.25;

  useEffect(() => {
    if (open) setZoomLevel(1);
  }, [open]);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(maxZoom, +(prev + zoomStep).toFixed(2)));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(minZoom, +(prev - zoomStep).toFixed(2)));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleDragStart = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (zoomLevel <= 1) return;
    const container = e.currentTarget;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    scrollStartRef.current = {
      left: container.scrollLeft,
      top: container.scrollTop,
    };
  };

  const handleDragMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!isDragging || zoomLevel <= 1) return;
    const container = e.currentTarget;
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    container.scrollLeft = scrollStartRef.current.left - deltaX;
    container.scrollTop = scrollStartRef.current.top - deltaY;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90vw', sm: '70vw', md: '50vw' },
          maxHeight: '75vh',
          minHeight: '75vh',
          bgcolor: '#fff',
          borderRadius: '10px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          <IconButton
            aria-label='zoom out'
            onClick={handleZoomOut}
            disabled={zoomLevel <= minZoom}
            size='small'
          >
            <Remove fontSize='small' />
          </IconButton>
          <Typography sx={{ minWidth: '54px', textAlign: 'center' }}>
            {Math.round(zoomLevel * 100)}%
          </Typography>
          <IconButton
            aria-label='zoom in'
            onClick={handleZoomIn}
            disabled={zoomLevel >= maxZoom}
            size='small'
          >
            <Add fontSize='small' />
          </IconButton>
          <Button
            variant='text'
            size='small'
            startIcon={<RestartAlt fontSize='small' />}
            onClick={handleResetZoom}
            disabled={zoomLevel === 1}
          >
            Reset
          </Button>
        </Stack>
        {imageUrl && (
          <Box
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor:
                zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            }}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
          >
            <Box
              component='img'
              src={imageUrl}
              alt='profile preview'
              sx={{
                width: `${zoomLevel * 100}%`,
                maxWidth: 'none',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '8px',
                transition: 'width 0.2s ease',
              }}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default memo(ImageViewModal);
